import { Card, Table, confirmDialog } from "@velocity-ehs/component-library";
import { Column } from "primereact/column";
import { ChangeEvent, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useGetRoles } from "../../../hooks/useGetRoles";
import PersonAssignRolesSlider from "./PersonAssignRolesSlider";
import { PersonAttr } from "./Person";
import usePersonRolesMutation, {
  AssignRolesInput,
  PersonRoleActions,
  RevokeRolesInput,
} from "../../../hooks/usePersonRolesMutation";
import * as Permissions from "../../supportFunctions/Permissions";
import UsersService from "@velocity-ehs/user-utils-module";

const defaultParams = {
  first: 0,
  rows: 20,
  page: 0,
  filters: {
    locationId: {
      value: [],
    },
    roleName: {
      value: [],
    },
  },
};

interface RolesProps {
  person: PersonAttr;
}

const Roles = ({ person }: RolesProps) => {
  const { t } = useTranslation();

  const [lazyParams, setLazyParams] = useState(defaultParams);
  const [showSlider, setShowSlider] = useState<boolean>(false);
  const [selected, setSelected] = useState<RevokeRolesInput[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const { roles, pagination, refetch, isFetching } = useGetRoles(
    person?.userId,
    lazyParams
  );

  useEffect(() => {
    pagination.totalElements && setTotalRecords(pagination.totalElements);
  }, [pagination.totalElements]);

  const canAssignRole = Permissions.IsAllowed(
    [Permissions.canAssignRole],
    UsersService.permissions
  );

  const { mutations } = usePersonRolesMutation(person?.userId);

  const bulkActions = (): any[] => {
    return [
      {
        label: t("assigned_roles_action_unassign"),
        command: confirmUnassign,
        disabled: selected.length === 0,
      },
      {
        label: t("assigned_roles_action_assign"),
        command: () => setShowSlider(true),
      },
    ];
  };

  const confirmUnassign = () => {
    confirmDialog({
      message: t("person_roles_unassign_confirm_message"),
      header: t("person_roles_unassign_confirm_header"),
      accept: unassignRoles,
      reject: () => setSelected([]),
      acceptLabel: t("action_unassign"),
      rejectLabel: t("universal_Cancel"),
    });
  };

  const unassignRoles = async () => {
    try {
      await mutations.mutate(PersonRoleActions.REVOKE, selected);
      refetch();
    } catch {}
  };

  const assignRoles = async (values: AssignRolesInput) => {
    try {
      await mutations.mutate(PersonRoleActions.ASSIGN, values);
      refetch();
    } catch {}
  };

  const onLocationsFilter = (locationIds: string[]) => {
    setLazyParams((previous: any) => {
      return {
        ...previous,
        filters: {
          ...previous.filters,
          locationId: { value: locationIds },
        },
      };
    });
  };

  const handleGlobalSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setLazyParams((previous) => ({
        ...previous,
        filters: {
          ...previous.filters,
          roleName: {
            value: [event.target.value],
          },
        },
      })),
    []
  );

  return (
    <>
      <h2>{t("assigned_roles_page_title")}</h2>
      <Card showHeader={false} full>
        <Table
          data={roles}
          isLoading={isFetching || mutations.isLoading}
          bulkActions={canAssignRole ? bulkActions : null}
          useBulkActionButtons
          totalRecords={totalRecords}
          params={lazyParams}
          onPage={(e) => setLazyParams(e)}
          fullPage={false}
          onSelectionChange={(e) => setSelected(e.value)}
          selectionMode="multiple"
          selection={selected}
          showLocationsFilter
          onLocationsFilter={onLocationsFilter}
          onGlobalSearch={handleGlobalSearch}
          testId="person-roles-table"
        >
          <Column field="role.name" header={t("meta_role")} />
          <Column
            field="role.description"
            header={t("universal_Description")}
          />
          <Column field="location.name" header={t("hierarchies_Location")} />
        </Table>
      </Card>
      <PersonAssignRolesSlider
        showSlider={showSlider}
        assignRoleCallback={assignRoles}
        closeSliderCallback={() => setShowSlider(false)}
      />
    </>
  );
};

export default Roles;
